import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    FormControlLabel,
    Checkbox,
    ToggleButtonGroup,
    ToggleButton,
    Tooltip,
} from '@mui/material';
import { TextIncrease, TextDecrease, CameraAlt } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { memberService } from '../services/memberService';
import { getConfiguration } from '../services/coreService';
import MemberPictureCamera from '../components/MemberPictureCamera';

// Default grey-scale empty head avatar (SVG data URI)
const DEFAULT_AVATAR = "data:image/svg+xml," + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 120">' +
    '<ellipse cx="50" cy="42" rx="36" ry="38" fill="#9e9e9e"/>' +
    '<path d="M18 82 Q50 100 82 82 L82 120 L18 120 Z" fill="#bdbdbd"/>' +
    '</svg>'
);

const MemberForm = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const membershipNumber = location.state?.membershipNumber || '';
    const defaultConsentMessage = "I consent to receive email and SMS notification for discount and promotion information.";
    
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        gender: '',
        dateOfBirth: '',
        address1: '',
        address2: '',
        city: '',
        province: '',
        country: '',
        postalCode: '',
    });

    const [allergies, setAllergies] = useState([]);
    const [selectedAllergies, setSelectedAllergies] = useState([]);
    const [otherAllergy, setOtherAllergy] = useState('');
    const [isOtherSelected, setIsOtherSelected] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitMessage, setSubmitMessage] = useState({ type: '', message: '' });
    const [consentMessage, setConsentMessage] = useState(defaultConsentMessage);
    const [privacyAgreement, setPrivacyAgreement] = useState('');
    const [privacyAgreed, setPrivacyAgreed] = useState(false);
    const [privacyError, setPrivacyError] = useState(false);
    const [notificationConsent, setNotificationConsent] = useState(true);
    const [largeTextMode, setLargeTextMode] = useState(false);
    const [capturedPicture, setCapturedPicture] = useState(null);
    const [cameraOpen, setCameraOpen] = useState(false);

    // Fetch allergies, DefaultLargeText, and load privacy/consent configs on component mount
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                // Fetch allergies
                const data = await memberService.getAllergies();
                setAllergies(data.allergies || []);
                
                // Default large text from config (Application ID 2): 1 = large, else or missing = small
                try {
                    const largeTextConfig = await getConfiguration(2, 'DefaultLargeText');
                    if (largeTextConfig && largeTextConfig.length > 0 && largeTextConfig[0].ConfigValue === '1') {
                        setLargeTextMode(true);
                    }
                } catch (configError) {
                    // Default to small text if config missing or error
                }
                
                // Load consent message from config (Application ID 2)
                try {
                    const consentConfig = await getConfiguration(2, 'ConsentMessage');
                    if (consentConfig && consentConfig.length > 0 && consentConfig[0].ConfigValue) {
                        setConsentMessage(consentConfig[0].ConfigValue);
                    }
                } catch (configError) {
                    console.log('Using default consent message');
                    // Keep default message if config fetch fails
                }
                
                // Load privacy agreement from config (Application ID 2)
                try {
                    const privacyConfig = await getConfiguration(2, 'PrivacyAgreement');
                    if (privacyConfig && privacyConfig.length > 0 && privacyConfig[0].ConfigValue) {
                        setPrivacyAgreement(privacyConfig[0].ConfigValue);
                    }
                } catch (configError) {
                    console.log('No privacy agreement configured');
                    // No privacy agreement configured, that's okay
                }
            } catch (err) {
                console.error('Error loading initial data:', err);
            }
        };
        loadInitialData();
    }, []);

    // Handle input changes
    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    // Handle allergy checkbox changes
    const handleAllergyChange = (allergyName) => (event) => {
        if (event.target.checked) {
            setSelectedAllergies(prev => [...prev, allergyName]);
        } else {
            setSelectedAllergies(prev => prev.filter(name => name !== allergyName));
        }
    };

    // Handle "Other" checkbox change
    const handleOtherAllergyChange = (event) => {
        setIsOtherSelected(event.target.checked);
        if (!event.target.checked) {
            setOtherAllergy('');
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        // First Name validation
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First Name is required';
        }

        // Last Name validation
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last Name is required';
        }

        // Phone validation
        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone is required';
        }

        // Email validation (optional; validate format only if provided)
        if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // City validation
        if (!formData.city.trim()) {
            newErrors.city = 'City is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (event) => {
        event.preventDefault();

        // Check privacy agreement first (if privacy agreement exists)
        if (privacyAgreement && !privacyAgreed) {
            setPrivacyError(true);
            setSubmitMessage({ 
                type: 'error', 
                message: 'Please confirm you have read and agree the Privacy Agreement' 
            });
            return;
        }

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);
        setSubmitMessage({ type: '', message: '' });
        setPrivacyError(false);

        try {
            // Concatenate allergies
            let allergyString = '';
            const allAllergyItems = [...selectedAllergies];
            
            // Add "Other" allergy if specified
            if (isOtherSelected && otherAllergy.trim()) {
                allAllergyItems.push(otherAllergy.trim());
            }
            
            // Join with ", " separator
            if (allAllergyItems.length > 0) {
                allergyString = allAllergyItems.join(', ');
            }

            // Prepare data for API
            const memberData = {
                membershipNumber: membershipNumber,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phone: formData.phone,
                email: formData.email,
                gender: formData.gender || null,
                dateOfBirth: formData.dateOfBirth || null,
                address1: formData.address1 || null,
                address2: formData.address2 || null,
                city: formData.city,
                province: formData.province || null,
                country: formData.country || null,
                postalCode: formData.postalCode || null,
                allergy: allergyString || null,
                notificationConsent: notificationConsent,
                createdDate: new Date().toISOString(),
            };

            const response = await memberService.createMember(memberData);

            if (response.success) {
                // Upsert picture if one was captured (memberId from create response)
                if (capturedPicture && response.memberId) {
                    try {
                        await memberService.upsertMemberPicture(response.memberId, capturedPicture);
                    } catch (picError) {
                        console.error('Error saving member picture:', picError);
                        // Don't fail the form - member was created successfully
                    }
                }
                setSuccess(true);
            } else {
                setSubmitMessage({ 
                    type: 'error', 
                    message: response.error || 'Failed to register member' 
                });
            }

        } catch (error) {
            console.error('Error registering member:', error);
            const errorMessage = error.response?.data?.error || 'Failed to register member';
            setSubmitMessage({ type: 'error', message: errorMessage });
        } finally {
            setSubmitting(false);
        }
    };

    // Success view
    if (success) {
        return (
            <Box sx={{ 
                minHeight: '100vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                bgcolor: 'grey.50',
                p: 2
            }}>
                <Paper elevation={3} sx={{ 
                    p: 4, 
                    maxWidth: 600, 
                    width: '100%',
                    borderRadius: 2,
                    textAlign: 'center'
                }}>
                    <Typography variant="h4" component="h1" gutterBottom color="primary">
                        Thank You!
                    </Typography>
                    
                    <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
                        Thank you for joining our membership program.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            bgcolor: 'grey.50',
            p: 2
        }}>
            <Paper elevation={3} sx={{ 
                p: 4, 
                maxWidth: 700, 
                width: '100%',
                borderRadius: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Typography 
                        variant="h4" 
                        component="h1" 
                        gutterBottom 
                        color="primary"
                        sx={{ flex: '1 1 auto', minWidth: 0, ...(largeTextMode && { fontSize: '2rem' }) }}
                    >
                        Member Signup Form
                    </Typography>
                    <ToggleButtonGroup
                        value={largeTextMode ? 'large' : 'small'}
                        exclusive
                        onChange={(e, value) => value != null && setLargeTextMode(value === 'large')}
                        aria-label="Text size"
                        size="medium"
                        sx={{
                            flexShrink: 0,
                            '& .MuiToggleButtonGroup-grouped': {
                                border: 1,
                                borderColor: 'divider',
                                px: 1.5,
                                py: 0.75,
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                },
                            },
                        }}
                    >
                        <Tooltip title="Small text">
                            <ToggleButton value="small" aria-label="Small text">
                                <TextDecrease fontSize="medium" />
                            </ToggleButton>
                        </Tooltip>
                        <Tooltip title="Large text">
                            <ToggleButton value="large" aria-label="Large text">
                                <TextIncrease fontSize="medium" />
                            </ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>
                </Box>
                
                {/* Membership Number Display */}
                <Box sx={{ 
                    mb: 2, 
                    p: 2, 
                    bgcolor: 'grey.100', 
                    borderRadius: 1,
                    textAlign: 'center'
                }}>
                    <Typography variant="body2" color="text.secondary" sx={largeTextMode ? { fontSize: '1.125rem' } : {}}>
                        Membership #
                    </Typography>
                    <Typography variant="h6" color="primary" sx={largeTextMode ? { fontSize: '1.5rem' } : {}}>
                        {membershipNumber}
                    </Typography>
                </Box>

                {/* Member Picture */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                    <Box
                        sx={{
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            border: '3px solid',
                            borderColor: 'divider',
                            bgcolor: 'grey.200',
                            flexShrink: 0,
                        }}
                    >
                        <img
                            src={capturedPicture || DEFAULT_AVATAR}
                            alt="Member"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                        />
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<CameraAlt />}
                        onClick={() => setCameraOpen(true)}
                        sx={{ mt: 1 }}
                    >
                        Take Picture
                    </Button>
                </Box>

                {submitMessage.message && (
                    <Alert 
                        severity={submitMessage.type} 
                        sx={{ mb: 3, ...(largeTextMode && { '& .MuiAlert-message': { fontSize: '1.25rem' } }) }}
                        onClose={() => setSubmitMessage({ type: '', message: '' })}
                    >
                        {submitMessage.message}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Basic Information Section */}
                    <Box>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, ...(largeTextMode && { fontSize: '1.5rem' }) }}>
                            Basic Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        {/* First Name and Last Name - one per row in large text mode */}
                        <Box sx={{ display: 'flex', flexDirection: largeTextMode ? 'column' : 'row', gap: 2, mb: 2 }}>
                            <TextField
                                fullWidth
                                label="First Name"
                                value={formData.firstName}
                                onChange={handleInputChange('firstName')}
                                error={!!errors.firstName}
                                helperText={errors.firstName}
                                required
                                InputProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                                InputLabelProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                                FormHelperTextProps={largeTextMode ? { sx: { fontSize: '1.125rem' } } : {}}
                            />
                            <TextField
                                fullWidth
                                label="Last Name"
                                value={formData.lastName}
                                onChange={handleInputChange('lastName')}
                                error={!!errors.lastName}
                                helperText={errors.lastName}
                                required
                                InputProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                                InputLabelProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                                FormHelperTextProps={largeTextMode ? { sx: { fontSize: '1.125rem' } } : {}}
                            />
                        </Box>

                        {/* Phone and Email */}
                        <Box sx={{ display: 'flex', flexDirection: largeTextMode ? 'column' : 'row', gap: 2, mb: 2 }}>
                            <TextField
                                fullWidth
                                label="Phone"
                                value={formData.phone}
                                onChange={handleInputChange('phone')}
                                error={!!errors.phone}
                                helperText={errors.phone}
                                required
                                InputProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                                InputLabelProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                                FormHelperTextProps={largeTextMode ? { sx: { fontSize: '1.125rem' } } : {}}
                            />
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange('email')}
                                error={!!errors.email}
                                helperText={errors.email}
                                InputProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                                InputLabelProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                                FormHelperTextProps={largeTextMode ? { sx: { fontSize: '1.125rem' } } : {}}
                            />
                        </Box>

                        {/* Gender and DOB */}
                        <Box sx={{ display: 'flex', flexDirection: largeTextMode ? 'column' : 'row', gap: 2 }}>
                            <FormControl fullWidth sx={largeTextMode ? { '& .MuiInputBase-root': { fontSize: '1.25rem' }, '& .MuiInputLabel-root': { fontSize: '1.25rem' } } : {}}>
                                <InputLabel>Gender</InputLabel>
                                <Select
                                    value={formData.gender}
                                    onChange={handleInputChange('gender')}
                                    label="Gender"
                                    MenuProps={largeTextMode ? { PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: '1.25rem' } } } } : {}}
                                >
                                    <MenuItem value="">
                                        <em>Select Gender</em>
                                    </MenuItem>
                                    <MenuItem value="Male">Male</MenuItem>
                                    <MenuItem value="Female">Female</MenuItem>
                                    <MenuItem value="Not Specify">Not Specify</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                label="Date of Birth"
                                type="date"
                                value={formData.dateOfBirth}
                                onChange={handleInputChange('dateOfBirth')}
                                InputLabelProps={{
                                    shrink: true,
                                    ...(largeTextMode && { sx: { fontSize: '1.25rem' } }),
                                }}
                                InputProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                            />
                        </Box>
                    </Box>

                    {/* Allergy Section */}
                    <Box>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, ...(largeTextMode && { fontSize: '1.5rem' }) }}>
                            Allergies
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: largeTextMode ? 'column' : 'row',
                            flexWrap: largeTextMode ? 'nowrap' : 'wrap', 
                            gap: 2,
                            ...(!largeTextMode && {
                                '& > *': {
                                    flex: '0 0 calc(33.333% - 16px)',
                                    minWidth: '200px'
                                }
                            })
                        }}>
                            {allergies.map((allergy) => (
                                <FormControlLabel
                                    key={allergy.Name}
                                    control={
                                        <Checkbox
                                            checked={selectedAllergies.includes(allergy.Name)}
                                            onChange={handleAllergyChange(allergy.Name)}
                                            sx={largeTextMode ? { '& .MuiSvgIcon-root': { fontSize: '1.75rem' } } : {}}
                                        />
                                    }
                                    label={allergy.Name}
                                    sx={largeTextMode ? { '& .MuiFormControlLabel-label': { fontSize: '1.25rem' } } : {}}
                                />
                            ))}
                        </Box>
                        
                        {/* Other Allergy Option - Always in its own row */}
                        <Box sx={{ mt: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isOtherSelected}
                                        onChange={handleOtherAllergyChange}
                                        sx={largeTextMode ? { '& .MuiSvgIcon-root': { fontSize: '1.75rem' } } : {}}
                                    />
                                }
                                label="Other, please specify"
                                sx={largeTextMode ? { '& .MuiFormControlLabel-label': { fontSize: '1.25rem' } } : {}}
                            />
                            
                            {/* Other Allergy Text Field */}
                            {isOtherSelected && (
                                <TextField
                                    fullWidth
                                    label="Other Allergy"
                                    value={otherAllergy}
                                    onChange={(e) => setOtherAllergy(e.target.value)}
                                    placeholder="Please specify other allergies"
                                    sx={{ mt: 1, ml: largeTextMode ? 5 : 4 }}
                                    size={largeTextMode ? 'medium' : 'small'}
                                    InputProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                                    InputLabelProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Address Section */}
                    <Box>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, ...(largeTextMode && { fontSize: '1.5rem' }) }}>
                            Address
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        {/* Address Line 1 */}
                        <TextField
                            fullWidth
                            label="Address Line 1"
                            value={formData.address1}
                            onChange={handleInputChange('address1')}
                            sx={{ mb: 2 }}
                            InputProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                            InputLabelProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                        />

                        {/* Address Line 2 */}
                        <TextField
                            fullWidth
                            label="Address Line 2"
                            value={formData.address2}
                            onChange={handleInputChange('address2')}
                            sx={{ mb: 2 }}
                            InputProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                            InputLabelProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                        />

                        {/* City and Province - one per row in large text mode */}
                        <Box sx={{ display: 'flex', flexDirection: largeTextMode ? 'column' : 'row', gap: 2, mb: 2 }}>
                            <TextField
                                fullWidth
                                label="City"
                                value={formData.city}
                                onChange={handleInputChange('city')}
                                error={!!errors.city}
                                helperText={errors.city}
                                required
                                InputProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                                InputLabelProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                                FormHelperTextProps={largeTextMode ? { sx: { fontSize: '1.125rem' } } : {}}
                            />
                            <TextField
                                fullWidth
                                label="Province"
                                value={formData.province}
                                onChange={handleInputChange('province')}
                                InputProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                                InputLabelProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                            />
                        </Box>

                        {/* Country and Postal Code */}
                        <Box sx={{ display: 'flex', flexDirection: largeTextMode ? 'column' : 'row', gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Country"
                                value={formData.country}
                                onChange={handleInputChange('country')}
                                InputProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                                InputLabelProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                            />
                            <TextField
                                fullWidth
                                label="Postal Code"
                                value={formData.postalCode}
                                onChange={handleInputChange('postalCode')}
                                InputProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                                InputLabelProps={largeTextMode ? { sx: { fontSize: '1.25rem' } } : {}}
                            />
                        </Box>
                    </Box>

                    {/* Privacy Agreement Section - Only show if privacy agreement exists */}
                    {privacyAgreement && (
                        <Box sx={{ mt: 3, mb: 0 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={largeTextMode ? { fontSize: '1.375rem' } : {}}>
                                Privacy Agreement:
                            </Typography>
                            <Paper 
                                variant="outlined" 
                                sx={{ 
                                    p: 2, 
                                    maxHeight: '350px', 
                                    overflowY: 'auto',
                                    backgroundColor: '#f9f9f9',
                                    mb: 1,
                                    ...(largeTextMode && {
                                        '& p, & li, & span, & div': { fontSize: '1.25rem', lineHeight: 1.6 },
                                        '& *': { fontSize: 'inherit' },
                                        fontSize: '1.25rem',
                                    })
                                }}
                            >
                                <div dangerouslySetInnerHTML={{ __html: privacyAgreement }} style={largeTextMode ? { fontSize: '1.25rem', lineHeight: 1.6 } : {}} />
                            </Paper>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={privacyAgreed}
                                        onChange={(e) => {
                                            setPrivacyAgreed(e.target.checked);
                                            if (e.target.checked) {
                                                setPrivacyError(false);
                                            }
                                        }}
                                        color="primary"
                                        sx={{
                                            '&.Mui-checked': {
                                                color: privacyError ? 'error.main' : 'primary.main',
                                            },
                                            color: privacyError ? 'error.main' : 'default',
                                            ...(largeTextMode && { '& .MuiSvgIcon-root': { fontSize: '1.75rem' } }),
                                        }}
                                    />
                                }
                                label="I confirm I have read and agree the Privacy Agreement above."
                                sx={{ 
                                    mt: 1, 
                                    mb: 0,
                                    '& .MuiFormControlLabel-label': {
                                        color: privacyError ? 'error.main' : 'inherit',
                                        ...(largeTextMode && { fontSize: '1.25rem' }),
                                    },
                                    border: privacyError ? '1px solid' : 'none',
                                    borderColor: privacyError ? 'error.main' : 'transparent',
                                    borderRadius: 1,
                                    p: privacyError ? 1 : 0,
                                }}
                            />
                        </Box>
                    )}

                    {/* Notification Consent Checkbox - Full Width */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={notificationConsent}
                                onChange={(e) => setNotificationConsent(e.target.checked)}
                                color="primary"
                                sx={largeTextMode ? { '& .MuiSvgIcon-root': { fontSize: '1.75rem' } } : {}}
                            />
                        }
                        label={consentMessage}
                        sx={{ 
                            mt: 1, 
                            mb: 1,
                            ...(largeTextMode && { '& .MuiFormControlLabel-label': { fontSize: '1.25rem' } }),
                        }}
                    />

                    {/* Submit Button */}
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={submitting}
                        sx={{ 
                            mt: 2, 
                            py: largeTextMode ? 2 : 1.5,
                            ...(largeTextMode && { fontSize: '1.25rem' }),
                        }}
                    >
                        {submitting ? (
                            <>
                                <CircularProgress size={largeTextMode ? 28 : 24} sx={{ mr: 1 }} />
                                Submitting...
                            </>
                        ) : (
                            'Submit'
                        )}
                    </Button>
                </Box>
            </Paper>

            <MemberPictureCamera
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onCapture={(dataUrl) => setCapturedPicture(dataUrl)}
            />
        </Box>
    );
};

export default MemberForm;

